import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Navigate, Route, Routes } from 'react-router-dom';

import AppTabs from '../../../components/designe/app-tabs/AppTabs';
import { Paths } from '../../../shared/paths';
import { useGetSyncProviders } from '../../../services/redux/api/syncApi';
import { useAccess } from '../../../hooks/useAccess';
import SyncRules from '../sync-rules/SyncRules';
import { ENTITY_TO_TAB } from './syncEntityConfig';
import { SyncMusclesContent } from './muscles/SyncMusclesContent';
import { SyncExercisesContent } from './exercises/SyncExercisesContent';

export function SyncInnerTabs() {
  const { t } = useTranslation();
  const { data: providers } = useGetSyncProviders();
  const { access } = useAccess();
  const base = `${Paths.adminDashboard.root}${Paths.adminDashboard.sync}`;
  const canViewSyncData =
    access?.syncRules?.canView ??
    access?.adminPanels?.canViewAdminPanel ??
    false;
  const canViewSyncRules = access?.syncRules?.canView ?? false;

  const syncDataTabs = useMemo(() => {
    if (!providers?.length) return [];
    const entities = [
      ...new Set(providers.flatMap((p) => p.entities)),
    ] as string[];
    return entities
      .filter((e) => ENTITY_TO_TAB[e])
      .map((entity) => {
        const { path, labelKey } = ENTITY_TO_TAB[entity];
        return {
          label: t(labelKey),
          path: `${base}${path}`,
          entity,
        };
      });
  }, [providers, base, t]);

  const tabsConfig = useMemo(() => {
    const tabs = [];

    if (canViewSyncRules) {
      tabs.push({
        label: t('adminDashboard.syncRules'),
        path: `${base}/sync-rules`,
      });
    }

    if (canViewSyncData) {
      tabs.push(...syncDataTabs.map(({ label, path }) => ({ label, path })));
    }

    return tabs;
  }, [canViewSyncRules, canViewSyncData, syncDataTabs, t, base]);

  const defaultTab =
    tabsConfig[0]?.path?.replace(`${base}/`, '')?.replace(/^\/+/, '') || '';

  return (
    <>
      <AppTabs
        config={tabsConfig.map(({ label, path }) => ({ label, path }))}
      />
      <Box sx={{ mt: 2 }}>
        <Routes>
          {canViewSyncRules && (
            <Route path="sync-rules" element={<SyncRules />} />
          )}
          {canViewSyncData && (
            <Route path="muscles" element={<SyncMusclesContent />} />
          )}
          {canViewSyncData && (
            <Route path="exercises" element={<SyncExercisesContent />} />
          )}
          <Route
            index
            element={<Navigate to={defaultTab || Paths.root} replace />}
          />
        </Routes>
      </Box>
    </>
  );
}
